/**
 * Column configuration for the Deliveries sub-tab table.
 *
 * Mirrors the Schedule Lines column-manager pattern (see
 * ../schedule-lines/types.ts): a per-column visibility/order config that is
 * persisted to localStorage per user and always merged against the DEFAULT
 * list so newly-added columns appear automatically.
 */

// Column configuration for visibility + ordering management.
export interface DeliveryColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  sortable: boolean;
  /** Backend ordering field (only used when ``sortable``). Falls back to id. */
  sortField?: string;
  minWidth: string;
  order: number;
  /** Header/label alignment. Cells set their own alignment per render. */
  align?: 'left' | 'right';
  /** CSV header name for export filtering. Columns without csvHeader are not in export. */
  csvHeader?: string;
}

// Default order (customer request 2026-07-15): On Time Status and Delivery
// Date sit immediately AFTER Appeal Status; everything else keeps its prior
// relative order.
export const DEFAULT_DELIVERY_COLUMNS: DeliveryColumnConfig[] = [
  // Customer review 11 Sep (stream I): every sortField below is one the GRN
  // list can order by (GoodsReceiptNoteViewSet.ordering_fields). Several named
  // a serializer field instead, so the header showed an arrow and the server
  // silently kept its default order; the computed money / appeal columns are
  // now plain headers rather than sort controls that do nothing.
  { id: 'grn_number', label: 'GRN Number', visible: true, sortable: false, minWidth: '140px', order: 0, csvHeader: 'GRN Number' },
  { id: 'view', label: 'View', visible: true, sortable: false, minWidth: '80px', order: 1 }, // Not in CSV
  { id: 'action', label: 'Action', visible: true, sortable: false, minWidth: '130px', order: 2 }, // Not in CSV
  { id: 'appeal_status', label: 'Appeal Status', visible: true, sortable: false, minWidth: '140px', order: 3 }, // Not in CSV
  { id: 'on_time_status', label: 'On Time Status', visible: true, sortable: true, sortField: 'on_time_status', minWidth: '140px', order: 4, csvHeader: 'On-Time Status' },
  { id: 'delivery_date', label: 'Delivery Date', visible: true, sortable: true, sortField: 'receipt_date', minWidth: '130px', order: 5, csvHeader: 'Receipt Date' },
  { id: 'customer', label: 'Customer', visible: true, sortable: true, sortField: 'po_line__po__customer__name', minWidth: '180px', order: 6, csvHeader: 'Customer' },
  { id: 'supplier_group', label: 'Supplier Group', visible: true, sortable: true, sortField: 'po_line__po__supplier__group__name', minWidth: '160px', order: 7 }, // Not in CSV
  { id: 'supplier', label: 'Supplier', visible: true, sortable: true, sortField: 'po_line__po__supplier__name', minWidth: '180px', order: 8, csvHeader: 'Supplier' },
  { id: 'po_number', label: 'PO Number', visible: true, sortable: true, sortField: 'po_line__po__po_number', minWidth: '130px', order: 9, align: 'right', csvHeader: 'PO Number' },
  { id: 'line_number', label: 'Line Number', visible: true, sortable: true, sortField: 'po_line__line_number', minWidth: '110px', order: 10, align: 'right', csvHeader: 'Line Number' },
  { id: 'sched_line', label: 'Sched Line', visible: true, sortable: false, minWidth: '110px', order: 11, align: 'right' }, // Not in CSV
  // Gated at runtime: only shown on PO-line tenants (!scheduleCollabActive).
  { id: 'po_line_status', label: 'PO Line Status', visible: true, sortable: true, sortField: 'po_line__status', minWidth: '130px', order: 12 }, // Not in CSV
  { id: 'part_number', label: 'Part Number', visible: true, sortable: true, sortField: 'po_line__part_number', minWidth: '140px', order: 13, csvHeader: 'Part Number' },
  { id: 'part_description', label: 'Part Description', visible: true, sortable: false, minWidth: '200px', order: 14, csvHeader: 'Part Description' },
  // Qty Ordered sortField is resolved dynamically in the header (schedule vs PO-line qty) depending on scheduleCollabActive.
  { id: 'qty_ordered', label: 'Qty Ordered', visible: true, sortable: true, sortField: 'po_line__quantity', minWidth: '120px', order: 15, align: 'right', csvHeader: 'Qty Ordered' },
  { id: 'qty_received', label: 'Qty Received', visible: true, sortable: true, sortField: 'quantity_received', minWidth: '130px', order: 16, align: 'right', csvHeader: 'Qty Received' },
  { id: 'unit_price', label: 'Unit Price', visible: true, sortable: true, sortField: 'po_line__unit_price', minWidth: '120px', order: 17, align: 'right', csvHeader: 'Unit Price' },
  { id: 'ppv', label: 'PPV', visible: true, sortable: true, sortField: 'po_line__ppv', minWidth: '100px', order: 18 }, // Not in CSV
  { id: 'currency', label: 'Currency', visible: true, sortable: true, sortField: 'po_line__po__currency', minWidth: '90px', order: 19, align: 'right', csvHeader: 'Currency' },
  { id: 'total_value', label: 'Total Value', visible: true, sortable: false, minWidth: '130px', order: 20, align: 'right' }, // Not in CSV
  // Gated at runtime: hidden for supplier viewers (!isSupplier).
  { id: 'unit_price_converted', label: 'Unit Price (Converted)', visible: true, sortable: false, minWidth: '160px', order: 21, align: 'right' }, // Not in CSV
  { id: 'total_value_converted', label: 'Total Value (Converted)', visible: true, sortable: false, minWidth: '170px', order: 22, align: 'right' }, // Not in CSV
  { id: 'release_note_number', label: 'Release Note Number', visible: true, sortable: true, sortField: 'packing_slip_number', minWidth: '150px', order: 23, align: 'right' }, // Not in CSV
  { id: 'mrp_date', label: 'MRP Date', visible: true, sortable: true, sortField: 'mrp_date', minWidth: '120px', order: 24 }, // Not in CSV
  { id: 'review_date', label: 'Review Date', visible: true, sortable: true, sortField: 'review_date', minWidth: '130px', order: 25 }, // Not in CSV
  // ODD = THE verdict anchor (slot -> line ODD -> delivery date), the date the
  // cell shows; `odd_date` is its SQL twin on the GRN list.
  { id: 'odd', label: 'ODD', visible: true, sortable: true, sortField: 'odd_date', minWidth: '120px', order: 26 }, // Not in CSV
];

export const DELIVERY_COLUMN_STORAGE_KEY = 'delivery_column_config';

/** Per-user storage key so a shared browser doesn't clobber layouts. */
function columnStorageKey(userId?: number | string): string {
  return userId != null
    ? `${DELIVERY_COLUMN_STORAGE_KEY}:${userId}`
    : DELIVERY_COLUMN_STORAGE_KEY;
}

/**
 * Load persisted delivery columns, merged against DEFAULT so that new columns
 * (added in later releases) always surface even for users with saved layouts.
 * Only visibility + order are taken from storage; all other metadata (label,
 * sortField, minWidth, align) is authoritative from DEFAULT.
 *
 * @param userId - Current user id, used to scope the storage key.
 * @returns Ordered column config array.
 */
export function loadDeliveryColumns(userId?: number | string): DeliveryColumnConfig[] {
  try {
    const stored = localStorage.getItem(columnStorageKey(userId));
    if (!stored) return DEFAULT_DELIVERY_COLUMNS;
    const parsed = JSON.parse(stored) as DeliveryColumnConfig[];
    const storedMap = new Map(parsed.map((c) => [c.id, c]));
    return DEFAULT_DELIVERY_COLUMNS.map((def) => {
      const saved = storedMap.get(def.id);
      return saved ? { ...def, visible: saved.visible, order: saved.order } : def;
    }).sort((a, b) => a.order - b.order);
  } catch {
    return DEFAULT_DELIVERY_COLUMNS;
  }
}

/**
 * Persist the current delivery column layout for a user.
 *
 * @param cols - Column config to persist.
 * @param userId - Current user id, used to scope the storage key.
 */
export function saveDeliveryColumns(cols: DeliveryColumnConfig[], userId?: number | string): void {
  localStorage.setItem(columnStorageKey(userId), JSON.stringify(cols));
}

/**
 * Filter and reorder CSV content based on visible columns.
 * Columns are reordered to match the user's column order.
 * Hidden columns are removed from the export.
 *
 * @param csvContent - Raw CSV string from backend
 * @param columns - Current column configuration (with visibility and order)
 * @returns Filtered and reordered CSV string
 */
export function filterDeliveryCsvByVisibleColumns(csvContent: string, columns: DeliveryColumnConfig[]): string {
  // Normalize line endings: convert \r\n to \n, then remove any stray \r
  const normalizedContent = csvContent.replace(/\r\n/g, '\n').replace(/\r/g, '');
  const lines = normalizedContent.trim().split('\n');
  if (lines.length === 0) return csvContent;

  // Parse header row to get column indices
  const headerRow = lines[0];
  const headers = parseCSVRow(headerRow);

  // Build mapping of CSV header -> original index
  const headerIndexMap = new Map<string, number>();
  headers.forEach((h, i) => headerIndexMap.set(h.trim(), i));

  // Get visible columns with csvHeader, sorted by user order
  const visibleWithCsv = columns
    .filter((c) => c.visible && c.csvHeader)
    .sort((a, b) => a.order - b.order);

  // Get indices of visible columns in user-defined order
  const visibleIndices: number[] = [];
  const newHeaders: string[] = [];
  for (const col of visibleWithCsv) {
    const idx = headerIndexMap.get(col.csvHeader!);
    if (idx !== undefined) {
      visibleIndices.push(idx);
      newHeaders.push(col.csvHeader!);
    }
  }

  // Also include columns not in our config (Qty Accepted, Qty Rejected, etc.) at the end
  const configuredHeaders = new Set(columns.map((c) => c.csvHeader).filter(Boolean));
  headers.forEach((h, i) => {
    if (!configuredHeaders.has(h.trim()) && !visibleIndices.includes(i)) {
      visibleIndices.push(i);
      newHeaders.push(h.trim());
    }
  });

  // Build new CSV with filtered/reordered columns
  const newLines: string[] = [newHeaders.join(',')];
  for (let i = 1; i < lines.length; i++) {
    const row = parseCSVRow(lines[i]);
    const newRow = visibleIndices.map((idx) => row[idx] ?? '');
    newLines.push(newRow.map((cell) => escapeCsvCell(cell)).join(','));
  }

  return newLines.join('\n');
}

/** Parse a CSV row handling quoted fields with commas. */
function parseCSVRow(row: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < row.length; i++) {
    const char = row[i];
    if (char === '"') {
      if (inQuotes && row[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current);
  return result;
}

/** Escape a cell value for CSV output. */
function escapeCsvCell(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}
